import { Component, ChangeDetectionStrategy } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import { TrygroupAccessReportExportService } from '../../../../services/trygroup-access-report-export/trygroup-access-report-export.service'
import {
  downloadBlob,
  getFileNameFromContentDisposition,
} from '../../../../util/blob-download'

const MAX_EXPORT_DAYS = 93

@UntilDestroy()
@Component({
  selector: 'app-trygroup-access-report-export-settings-page',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  template: `
    <section class="settings-panel">
      <div class="panel-header">
        <div>
          <h3>アクセス状況レポートエクスポート</h3>
          <p>
            ログイン・アセスメント参照回数の日次集計をExcel（シート名
            Aggregate）で出力します。
          </p>
        </div>
      </div>

      <div class="date-range">
        <label>
          開始日
          <input
            type="date"
            [max]="maxDate"
            [(ngModel)]="startDate"
            (ngModelChange)="onDateChange()"
          />
        </label>
        <label>
          終了日
          <input
            type="date"
            [max]="maxDate"
            [(ngModel)]="endDate"
            (ngModelChange)="onDateChange()"
          />
        </label>
      </div>

      @if (validationError) {
        <p class="error">{{ validationError }}</p>
      }

      <button
        mat-flat-button
        color="primary"
        type="button"
        [disabled]="isExporting || !!validationError"
        (click)="downloadAccessReport()"
      >
        <span class="button-content">
          @if (isExporting) {
            <mat-spinner diameter="18"></mat-spinner>
          } @else {
            <mat-icon>download</mat-icon>
          }
          {{ isExporting ? 'エクスポート中...' : 'Excelダウンロード' }}
        </span>
      </button>

      @if (noDataMessage) {
        <p class="info" aria-live="polite">{{ noDataMessage }}</p>
      }
      @if (errorMessage) {
        <p class="error" aria-live="polite">{{ errorMessage }}</p>
      }
    </section>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .settings-panel {
        padding: 20px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgb(15 23 42 / 6%);
      }

      .panel-header {
        display: flex;
        gap: 16px;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: 18px;
      }

      h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 800;
      }

      p {
        margin: 6px 0 0;
        color: #64748b;
        font-size: 13px;
      }

      .date-range {
        display: flex;
        gap: 16px;
        align-items: center;
        margin-bottom: 16px;
      }

      .date-range label {
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 13px;
        color: #374151;
      }

      .date-range input[type='date'] {
        padding: 6px 10px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 14px;
      }

      .button-content {
        display: inline-flex;
        gap: 8px;
        align-items: center;
      }

      .button-content mat-icon {
        width: 18px;
        height: 18px;
        font-size: 18px;
      }

      .error {
        margin-top: 16px;
        color: #dc2626;
        font-weight: 700;
      }

      .info {
        margin-top: 16px;
        color: #64748b;
      }
    `,
  ],
})
export class TrygroupAccessReportExportSettingsPageComponent {
  startDate: string
  endDate: string
  isExporting = false
  validationError = ''
  errorMessage = ''
  noDataMessage = ''

  constructor(
    private trygroupAccessReportExportService: TrygroupAccessReportExportService,
  ) {
    this.startDate = this.maxDate
    this.endDate = this.maxDate
  }

  /**
   * 集計は JST 前日分まで存在するため、指定可能な最大日付は前日。
   * 画面を開いたまま日付を跨いでも追従するよう都度計算する。
   */
  get maxDate(): string {
    return formatJstYesterday()
  }

  onDateChange(): void {
    this.validationError = validateDateRange(
      this.startDate,
      this.endDate,
      this.maxDate,
    )
  }

  /** アクセス状況レポートを取得し、サーバー指定のファイル名でダウンロードする。 */
  downloadAccessReport(): void {
    if (this.isExporting) {
      return
    }

    // バリデーションエラーで早期 return しても前回の結果メッセージが残らないよう先にクリアする
    this.errorMessage = ''
    this.noDataMessage = ''

    this.validationError = validateDateRange(
      this.startDate,
      this.endDate,
      this.maxDate,
    )
    if (this.validationError) {
      return
    }

    this.isExporting = true
    this.trygroupAccessReportExportService
      .exportAccessReport(this.startDate, this.endDate)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          this.isExporting = false

          // 0件時は 200 + JSON が返る（エラーステータスはグローバルインターセプターの
          // 汎用スナックバーを併発させるため使わない）。Content-Type で判定する。
          const contentType = response.headers.get('Content-Type') ?? ''
          if (contentType.includes('application/json') || !response.body) {
            this.noDataMessage = 'エクスポート対象データがありません。'
            return
          }

          const fileName = getFileNameFromContentDisposition(
            response.headers.get('Content-Disposition'),
            'access-report.xlsx',
          )
          downloadBlob(response.body, fileName)
        },
        error: (error: unknown) => {
          this.isExporting = false
          this.errorMessage =
            error instanceof HttpErrorResponse && error.status === 403
              ? 'アクセスが許可されていません。'
              : 'アクセス状況レポートのエクスポートに失敗しました。'
        },
      })
  }
}

function validateDateRange(
  startDate: string,
  endDate: string,
  maxDate: string,
): string {
  const start = new Date(startDate)
  const end = new Date(endDate)
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return '開始日と終了日を入力してください。'
  }
  const diffDays = Math.floor(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  )
  if (diffDays < 0) {
    return '終了日は開始日以降の日付を指定してください。'
  }
  if (diffDays > MAX_EXPORT_DAYS) {
    return `期間は${MAX_EXPORT_DAYS}日以内で指定してください。`
  }
  if (endDate > maxDate) {
    return '終了日には前日以前の日付を指定してください。'
  }
  return ''
}

/** JST 基準の前日を YYYY-MM-DD で返す（date picker 初期値・最大値用）。 */
function formatJstYesterday(): string {
  const jstNow = new Date(Date.now() + 9 * 60 * 60 * 1000)
  jstNow.setUTCDate(jstNow.getUTCDate() - 1)
  return jstNow.toISOString().slice(0, 10)
}
